import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useNotificationStore } from '../../../components/notificationStore';
import { useUserStore } from '../../../components/userStore';

const FundsForm = () => {
	const { user } = useUserStore();
	const navigate = useNavigate();
	const { fundId } = useParams();
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useNotificationStore();
	const fundQuery = useQuery({
		enabled: !!fundId,
		queryKey: ['fund', user.coord_agency_id, fundId],
		queryFn: async () => {
			const { data } = await axios.get(
				`coord_agencies/${user.coord_agency_id}/funds/${fundId}`,
			);
			return data;
		},
		initialData: () =>
			queryClient
				.getQueryData(['fundsList', user.coord_agency_id])
				?.find(({ id }) => id == fundId),
	});
	const fundMutation = useMutation({
		mutationFn: async (formData) => {
			let data;
			if (fundId) {
				({ data } = await axios.put(
					`coord_agencies/${user.coord_agency_id}/funds/${fundId}`,
					{
						...formData,
						amount: 0,
						coord_agency_id: user.coord_agency_id,
					},
				));
			} else {
				({ data } = await axios.post(
					`coord_agencies/${user.coord_agency_id}/funds`,
					{
						...formData,
						amount: 0,
						coord_agency_id: user.coord_agency_id,
					},
				));
			}
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['fundsList', user.coord_agency_id],
			});
			navigate('/funds');
			showSuccess(`Fund ${fundId ? 'updated' : 'created'} successfully`);
		},
		onError: (error) => {
			showError(
				error?.response?.data?.message ||
					`Error ${fundId ? 'updating' : 'creating'} fund`,
			);
		},
	});
	const fundDeleteMutation = useMutation({
		mutationFn: async () => {
			await axios.delete(
				`coord_agencies/${user.coord_agency_id}/funds/${fundId}`,
			);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['fundsList', user.coord_agency_id],
			});
			navigate('/funds');
			showSuccess('Fund deleted successfully');
			return false;
		},
		onError: (error) => {
			showError(error?.response?.data?.message || 'Error deleting fund');
			return false;
		},
	});
	const { register, handleSubmit } = useForm({ values: fundQuery.data });

	const isEditing = !!fundId;

	return (
		<>
			<div className="breadcrumbs">
				<ul>
					<li>
						<Link to="/" className="link">
							Home
						</Link>
					</li>
					<li>
						<Link to="/funds" className="link">
							Funds
						</Link>
					</li>
					<li>{isEditing ? 'Update' : 'Add'} Fund</li>
				</ul>
			</div>
			<div className="clear-both px-4 py-4 mt-2 bg-white rounded-lg">
				<h2>Funds Management - {isEditing ? 'Update' : 'Add'} Fund</h2>
				<div className="divider" />
				<form onSubmit={handleSubmit(fundMutation.mutate)}>
					<div className="form-control">
						<label htmlFor="acct-num" className="label">
							<span className="label-text">
								Account number <span className="text-error">*</span>
							</span>
						</label>
						<input
							type="text"
							name="acct-num"
							id="acct-num"
							autoComplete="off"
							className="input input-bordered"
							{...register('acct_num', { required: true })}
						/>
					</div>
					<div className="form-control">
						<label htmlFor="fund-name" className="label">
							<span className="label-text">
								Fund name <span className="text-error">*</span>
							</span>
						</label>
						<input
							type="text"
							name="fund-name"
							id="fund-name"
							autoComplete="off"
							className="input input-bordered"
							{...register('name', { required: true })}
						/>
					</div>
					<div className="form-control">
						<label htmlFor="fund-description" className="label">
							<span className="label-text">Description</span>
						</label>
						<textarea
							name="fund-description"
							id="fund-description"
							autoComplete="off"
							className="textarea textarea-bordered"
							{...register('description')}
						/>
					</div>
					{/* <div className="form-control sm:col-span-2">
						<label htmlFor="fund-amount" className="label">
							<span className="label-text">
								Fund amount <span className="text-error">*</span>
							</span>
						</label>
						<input
							type="text"
							name="fund-amount"
							id="fund-amount"
							autoComplete="off"
							className="input input-bordered"
							{...register('amount', { required: true })}
						/>
					</div> */}
					<div className="flex w-full gap-4 mt-4">
						<div className="flex-grow">
							<button
								className="btn btn-warning"
								onClick={fundDeleteMutation.mutate}
							>
								Delete
							</button>
						</div>
						<div className="flex self-end gap-4">
							<Link
								to="/funds"
								className="btn btn-outline btn-primary btn-wide"
							>
								Cancel
							</Link>
							<button className="btn btn-primary btn-wide" type="submit">
								Save
							</button>
						</div>
					</div>
				</form>
			</div>
		</>
	);
};

export default FundsForm;
